import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Chip,
  Grid,
} from '@material-ui/core';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useThunkDispatch, useTypedSelector } from 'hooks';
import { setComisionistFilter, fetchComisionistList } from 'redux/actions/comisionist.actions';
import { fetchOffices } from 'redux/actions/catalog.actions';
import { allZones } from 'redux/actions/zones.actions';
import { ComisionistFilterParams } from 'types/redux/state';
import { shouldReload } from 'utils/state.handlers';
import {
  comisionistPrimaryStatusLabels,
  comisionistSecondaryStatusLabels,
  getComisionistPrimaryStatusStyle,
  getComisionistSecondaryStatusStyle,
  comisionistStatusChipSx,
} from './utils/statusStyles';
import { PrimaryStatus, SecondaryStatus } from 'types/api/responses';

export function FilterComisionist() {
  const dispatch = useThunkDispatch();
  const { filter, isLoading } = useTypedSelector((s) => s.comisionist);
  const zones = useTypedSelector((s) => s.zones);
  const branchOffices = useTypedSelector((s) => s.catalog.offices);

  const [localFilter, setLocalFilter] = useState(filter);
  const [filterZoneId, setFilterZoneId] = useState(0);
  const [filterTipo, setFilterTipo] = useState('none');
  const [filterOfficeId, setFilterOfficeId] = useState('none');
  const [selectedStatus, setSelectedStatus] = useState<string[]>(['Todos']);
  const [selectedDocStatus, setSelectedDocStatus] = useState<string[]>(['Todos']);
  const [accountError, setAccountError] = useState<string>('');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerAutoFilter = useCallback(
    (newFilter: typeof localFilter) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        dispatch(setComisionistFilter(newFilter));
        dispatch(fetchComisionistList({ page: 1, size: 10, filter: newFilter }));
      }, 600);
    },
    [dispatch]
  );

  useEffect(() => {
    if (shouldReload(zones)) dispatch(allZones());
  }, [zones, dispatch]);

  const handleFilterChange = (field: string, value: any) => {
    setLocalFilter((prev: ComisionistFilterParams) => ({
      ...prev,
      [field]: value || undefined,
    }));
  };

  const applyFilter = (filterToApply: ComisionistFilterParams) => {
    setLocalFilter(filterToApply);
    dispatch(setComisionistFilter(filterToApply));
    dispatch(fetchComisionistList({ page: 1, size: 10, filter: filterToApply }));
  };

  const handleSearch = () => applyFilter(localFilter);

  const handleRefresh = () => {
    setSelectedStatus(['Todos']);
    setSelectedDocStatus(['Todos']);
    setFilterZoneId(0);
    setFilterTipo('none');
    setFilterOfficeId('none');
    applyFilter({ ZoneId: undefined });
  };

  const handleZoneChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const zoneId = Number(event.target.value);
    setFilterZoneId(zoneId);
    setFilterOfficeId('none');
    if (zoneId > 0) dispatch(fetchOffices({ ZoneId: zoneId }));
    applyFilter({ ...localFilter, ZoneId: zoneId > 0 ? zoneId : undefined, OfficeId: undefined });
  };

  const handleOfficeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const val = event.target.value as string;
    setFilterOfficeId(val);
    applyFilter({ ...localFilter, OfficeId: val === 'none' ? undefined : val });
  };

  const officesInZone = branchOffices.data.filter((o) => o.zone.id === filterZoneId);

  const handleDownloadReport = () => console.log('Descargar reporte');

  const getFilterChipStyle = (status: string, selected: boolean, type: 'primary' | 'secondary') => {
    if (!selected || status === 'Todos') return {};
    const style =
      type === 'primary'
        ? getComisionistPrimaryStatusStyle(status as PrimaryStatus)
        : getComisionistSecondaryStatusStyle(status as SecondaryStatus);
    return { ...comisionistStatusChipSx, backgroundColor: style.backgroundColor, color: style.color, border: style.border };
  };

  const sectionLabel = (text: string) => (
    <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, letterSpacing: '0.05em', marginBottom: '6px' }}>
      {text}
    </div>
  );

  return (
    <Box className="filters-panel-comisionist" p={2} m={2}>
      <Grid container spacing={2} style={{ display: 'flex', alignItems: 'center'}}>
        {/* ── Col 1 (sm=6): inputs en 2 filas de 3 ── */}
        <Grid item xs={12} sm={6}>
          <br />
          <Grid container spacing={1}  style={{ marginTop: '8px' }}>
            <Grid item xs={12} sm={4}>
              <TextField
                placeholder="Cuenta"
                size="small"
                variant="outlined"
                fullWidth
                value={localFilter.account || ''}
                error={!!accountError}
                helperText={accountError || ' '}
                inputProps={{ readOnly: isLoading, maxLength: 7, inputMode: 'numeric', pattern: '[0-9]*' }}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, '');

                  // Actualizar el estado local inmediatamente (para que el input responda al escribir)
                  const newFilter = { ...localFilter, account: raw || undefined };
                  setLocalFilter(newFilter);

                  // Validación visual en tiempo real
                  if (raw.length === 0) {
                    setAccountError('');
                  } else if (raw.length < 7) {
                    setAccountError('La cuenta debe tener 7 dígitos');
                  } else {
                    setAccountError('');
                  }

                  // Disparar filtro con debounce: esperar a que el usuario deje de escribir
                  if (debounceRef.current) clearTimeout(debounceRef.current);
                  debounceRef.current = setTimeout(() => {
                    // Solo aplicar si el campo está vacío o tiene exactamente 7 dígitos
                    if (raw.length === 0 || raw.length === 7) {
                      dispatch(setComisionistFilter(newFilter));
                      dispatch(fetchComisionistList({ page: 1, size: 10, filter: newFilter }));
                    }
                  }, 600);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                placeholder="Nombre Comisionista"
                size="small"
                variant="outlined"
                fullWidth
                value={localFilter.name || ''}
                helperText=" "
                inputProps={{ readOnly: isLoading }}
                onChange={(e) => {
                  const newFilter = { ...localFilter, name: e.target.value || undefined };
                  setLocalFilter(newFilter);
                  triggerAutoFilter(newFilter);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  label="Tipo"
                  value={filterTipo}
                  onChange={(e) => {
                    const val = e.target.value as string;
                    setFilterTipo(val);
                    applyFilter({ ...localFilter, type: val === 'none' ? undefined : val });
                  }}
                  disabled={isLoading}
                >
                  <MenuItem value="none">Sin Filtrar</MenuItem>
                  <MenuItem value="Formal">Formal</MenuItem>
                  <MenuItem value="Informal">Informal</MenuItem>
                </Select>
                <FormHelperText> </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Zona</InputLabel>
                <Select
                  label="Zona"
                  value={filterZoneId}
                  onChange={handleZoneChange}
                  disabled={isLoading || zones.isLoading}
                >
                  <MenuItem value={0}>Sin Filtrar</MenuItem>
                  {zones.zones.map((zone) => (
                    <MenuItem key={zone.id} value={Number(zone.id)}>{zone.name}</MenuItem>
                  ))}
                </Select>
                <FormHelperText> </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Sucursal</InputLabel>
                <Select
                  label="Sucursal"
                  value={filterOfficeId}
                  onChange={handleOfficeChange}
                  disabled={isLoading || branchOffices.isLoading || filterZoneId === 0}
                >
                  <MenuItem value="none">Sin Filtrar</MenuItem>
                  {officesInZone.map((o) => (
                    <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                  ))}
                </Select>
                <FormHelperText> </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <DateTimePicker
                label="Actualizado Después"
                value={localFilter.updatedAfter ? new Date(localFilter.updatedAfter as any) : null}
                onChange={(date: any) => {
                  const newFilter = { ...localFilter, updatedAfter: date?.toISOString() || undefined };
                  setLocalFilter(newFilter);
                  applyFilter(newFilter);
                }}
                views={['year', 'month', 'day']}
                slotProps={{ textField: { variant: 'outlined', size: 'small', fullWidth: true, helperText: ' ' } }}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* ── Col 2 (sm=4): chips de estado ── */}
        <Grid item xs={12} sm={4}>
          <Box mb={1}>
            {sectionLabel('ESTADO CLIENTE')}
            <Box display="flex" flexWrap="wrap" style={{ gap: '6px' }}>
              {(['Todos', 'New', 'Active', 'Inactive'] as const).map((status) => {
                const isSelected = selectedStatus.includes(status);
                return (
                  <Chip
                    key={status}
                    label={status === 'Todos' ? 'Todos' : comisionistPrimaryStatusLabels[status]}
                    disabled={isLoading}
                    onClick={() => {
                      if (status === 'Todos') {
                        setSelectedStatus(['Todos']);
                        applyFilter({ ...localFilter, primaryStatus: undefined });
                      } else {
                        setSelectedStatus([status]);
                        applyFilter({ ...localFilter, primaryStatus: status });
                      }
                    }}
                    variant={isSelected ? 'default' : 'outlined'}
                    size="small"
                    style={getFilterChipStyle(status, isSelected, 'primary')}
                  />
                );
              })}
            </Box>
          </Box>
          <Box>
            {sectionLabel('ESTADO DOCUMENTAL')}
            <Box display="flex" flexWrap="wrap" style={{ gap: '6px' }}>
              {(['Todos', 'PendingDocuments', 'DocsOK', 'PendingVerification'] as const).map((status) => {
                const isSelected = selectedDocStatus.includes(status);
                return (
                  <Chip
                    key={status}
                    label={status === 'Todos' ? 'Todos' : comisionistSecondaryStatusLabels[status]}
                    disabled={isLoading}
                    onClick={() => {
                      if (status === 'Todos') {
                        setSelectedDocStatus(['Todos']);
                        applyFilter({ ...localFilter, secondaryStatus: undefined });
                      } else {
                        setSelectedDocStatus([status]);
                        applyFilter({ ...localFilter, secondaryStatus: status });
                      }
                    }}
                    variant={isSelected ? 'default' : 'outlined'}
                    size="small"
                    style={getFilterChipStyle(status, isSelected, 'secondary')}
                  />
                );
              })}
            </Box>
          </Box>
        </Grid>

        {/* ── Col 3 (sm=2): botones centrados verticalmente ── */}
        <Grid item xs={12} sm={2}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="stretch"
            style={{ gap: '8px' }}
          >
            <Button
              startIcon={<SearchIcon />}
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSearch}
              disabled={isLoading}
              fullWidth
            >
              Buscar
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              variant="outlined"
              size="small"
              onClick={handleRefresh}
              disabled={isLoading}
              fullWidth
            >
              Refrescar
            </Button>
     {/*        <Button
              startIcon={<FileDownloadIcon />}
              variant="outlined"
              size="small"
              onClick={handleDownloadReport}
              disabled={isLoading}
              fullWidth
            >
              Reporte
            </Button> */}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
